import React from 'react';
import { Building2, TrendingUp, ClipboardCheck, Boxes, Hammer, BarChart2, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage, usePageMeta } from '../context/LanguageContext';
interface Service {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
}
const ICONS = [<Building2 className="w-7 h-7 text-white" data-id="element-227" />, <TrendingUp className="w-7 h-7 text-white" data-id="element-228" />, <ClipboardCheck className="w-7 h-7 text-white" data-id="element-229" />, <Boxes className="w-7 h-7 text-white" data-id="element-230" />, <Hammer className="w-7 h-7 text-white" data-id="element-231" />, <BarChart2 className="w-7 h-7 text-white" data-id="element-232" />];
function ServiceIcon({
  icon
}: {
  icon: React.ReactNode;
}) {
  return <div className="shrink-0 w-16 h-16 rounded-xl bg-navy flex items-center justify-center shadow-sm" data-id="element-233">
      {icon}
    </div>;
}
function ServiceContent({
  service
}: {
  service: Service;
}) {
  return <div className="flex-1" data-id="element-234">
      <h3 className="text-navy font-poppins font-bold text-xl md:text-2xl uppercase tracking-wide mb-2" data-id="element-235">
        {service.title}
      </h3>
      <p className="text-brandOrange font-medium mb-4" data-id="element-236">{service.subtitle}</p>
      <p className="text-gray-500 text-sm md:text-base leading-relaxed max-w-3xl" data-id="element-237">
        {service.description}
      </p>
    </div>;
}
export function Services() {
  const {
    t
  } = useLanguage();
  usePageMeta(t.meta.services.title, t.meta.services.description);
  const services: Service[] = t.servicesPage.cards.map((card, i) => ({
    icon: ICONS[i],
    title: card.title,
    subtitle: card.subtitle,
    description: card.description
  }));
  return <div className="w-full bg-white" data-id="element-238">
      {/* Hero Banner */}
      <section className="w-full bg-navy py-20 md:py-24" data-id="element-239">
        <div className="max-w-6xl mx-auto px-6 lg:px-8" data-id="element-240">
          <span className="text-brandOrange text-xs font-bold tracking-[0.15em] uppercase mb-4 block" data-id="element-241">
            {t.servicesPage.eyebrow}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-[52px] font-poppins font-bold text-white leading-[1.1] max-w-3xl" data-id="element-242">
            {t.servicesPage.heading}
          </h1>
        </div>
      </section>

      {/* Services List */}
      <section className="w-full py-16 md:py-20" data-id="element-243">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 flex flex-col gap-8" data-id="element-244">
          {services.map((service, index) => {
          const iconRight = index % 2 === 1;
          return <article key={service.title} className="bg-white border border-borderColor rounded-2xl p-8 md:p-10 shadow-[0_2px_16px_-6px_rgba(0,0,0,0.06)] hover:shadow-md transition-shadow duration-300" data-id="element-245">
                <div className="flex flex-col sm:flex-row items-start gap-6 md:gap-8" data-id="element-246">
                  {iconRight ? <>
                      <ServiceContent service={service} data-id="element-247" />
                      <div className="order-first sm:order-last" data-id="element-248">
                        <ServiceIcon icon={service.icon} data-id="element-249" />
                      </div>
                    </> : <>
                      <ServiceIcon icon={service.icon} data-id="element-250" />
                      <ServiceContent service={service} data-id="element-251" />
                    </>}
                </div>
              </article>;
        })}
        </div>
      </section>

      {/* Contact CTA Block */}
      <section className="w-full bg-[#f8f9fa] py-16" data-id="element-252">
        <div className="max-w-6xl mx-auto px-6 lg:px-8" data-id="element-253">
          <div className="bg-navy rounded-2xl px-8 py-10 md:px-12 md:py-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8" data-id="element-254">
            <div data-id="element-255">
              <h2 className="text-2xl md:text-3xl font-poppins font-bold text-white mb-3" data-id="element-256">
                {t.common.howCanWeHelp}
              </h2>
              <p className="text-blue-100 mb-6 max-w-xl" data-id="element-257">
                {t.common.ctaSubtitle}
              </p>
              <div className="flex flex-wrap gap-x-8 gap-y-3" data-id="element-258">
                {t.servicesPage.ctaBenefits.map(item => <div key={item} className="flex items-center gap-2" data-id="element-259">
                    <CheckCircle2 className="w-4 h-4 text-brandOrange shrink-0" data-id="element-260" />
                    <span className="text-white text-sm font-medium" data-id="element-261">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
            <Link to="/contact" className="shrink-0 bg-brandOrange hover:bg-brandOrange-hover text-white px-7 py-3.5 rounded font-semibold flex items-center gap-2 transition-colors shadow-lg shadow-brandOrange/20" data-id="element-262">
              {t.common.requestConsulting}
              <ArrowRight className="w-4 h-4" data-id="element-263" />
            </Link>
          </div>
        </div>
      </section>
    </div>;
}